import { Box, Chip, Divider, Stack, Tooltip, Typography } from "@mui/material";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import { useEffect, useState } from "react";
import { palette } from "../../../theme";
import dayjs from "dayjs";

const VAL = ({ children }) => (
  <span style={{ color: palette.primary.main }}>{children ?? "—"}</span>
);

const IfaceCard = ({ iface, index, forceOffline }) => {
  const isUp = !forceOffline && !!iface.status_up;
  return (
    <Box sx={{
      border: "1px solid rgba(69,144,149,0.15)",
      borderRadius: 1.5,
      p: 1.5,
      backgroundColor: "rgba(69,144,149,0.03)",
    }}>
      <Box display="flex" alignItems="center" gap={1} mb={1}>
        <Typography sx={{ fontWeight: 700, fontSize: 13, color: "#e8e8e8" }}>
          LAN {index + 1}
        </Typography>
        <Chip
          label={isUp ? "Online" : "Offline"}
          size="small"
          sx={{
            height: 18, fontSize: "0.65rem", fontWeight: 700, ml: "auto",
            backgroundColor: isUp ? "rgba(39,174,96,0.18)" : "rgba(150,50,50,0.18)",
            color: isUp ? "#27ae60" : "#c0392b",
            border: `1px solid ${isUp ? "rgba(39,174,96,0.4)" : "rgba(192,57,43,0.4)"}`,
          }}
        />
      </Box>
      <Stack spacing={0.4}>
        {iface.ip && (
          <Typography variant="caption" color="text.secondary">
            IP: <VAL>{iface.ip}{iface.subnet ? ` / ${iface.subnet}` : ""}</VAL>
          </Typography>
        )}
        {iface.gateway && (
          <Typography variant="caption" color="text.secondary">
            Gateway: <VAL>{iface.gateway}</VAL>
          </Typography>
        )}
        {Array.isArray(iface.dns) && iface.dns.length > 0 && (
          <Typography variant="caption" color="text.secondary">
            DNS: <VAL>{iface.dns.join(", ")}</VAL>
          </Typography>
        )}
        {iface.mac && (
          <Typography variant="caption" color="text.secondary" sx={{ fontFamily: "monospace" }}>
            MAC: <VAL>{iface.mac}</VAL>
          </Typography>
        )}
      </Stack>
    </Box>
  );
};

const MirroringChip = ({ mirroring }) => {
  if (mirroring === "ok") {
    return (
      <Chip
        icon={<CheckCircleOutlineIcon sx={{ fontSize: "0.85rem !important" }} />}
        label="OK"
        size="small"
        sx={{
          height: 22, fontSize: "0.72rem", fontWeight: 700,
          backgroundColor: "rgba(39,174,96,0.18)",
          color: "#27ae60",
          border: "1px solid rgba(39,174,96,0.4)",
          "& .MuiChip-icon": { color: "#27ae60" },
        }}
      />
    );
  }
  if (mirroring === "alert" || mirroring === "no_data") {
    const title = mirroring === "no_data"
      ? "No data received in the last 5 minutes — possible mirroring misconfiguration"
      : "Possible mirroring misconfiguration — only broadcast events detected in the last 5 minutes";
    return (
      <Tooltip title={title} arrow>
        <Chip
          icon={<WarningAmberIcon sx={{ fontSize: "0.85rem !important" }} />}
          label="Alert"
          size="small"
          sx={{
            height: 22, fontSize: "0.72rem", fontWeight: 700,
            backgroundColor: "rgba(230,126,34,0.18)",
            color: "#e67e22",
            border: "1px solid rgba(230,126,34,0.4)",
            "& .MuiChip-icon": { color: "#e67e22" },
            cursor: "help",
          }}
        />
      </Tooltip>
    );
  }
  return <Typography sx={{ fontSize: 14, color: palette.primary.main }}>—</Typography>;
};

const DeviceInfo = ({ deviceInfo }) => {
  const [info, setInfo] = useState({});
  const threshold = dayjs().subtract(60, "minute");

  useEffect(() => { setInfo(deviceInfo); }, [deviceInfo]);

  const ifaces   = info?.interface ?? [];
  const statusTs = typeof info?.status === "string" ? info.status : info?.status?.timeStamp;
  const isOnline = statusTs ? dayjs(statusTs).isAfter(threshold) : false;

  const firmware = (() => {
    const raw = info?.status?.releaseFw ?? info?.releaseFw;
    if (!raw) return null;
    const parts = raw.split(" - ");
    return parts.length > 1 ? parts[1].trim() : raw.trim();
  })();

  return (
    <Box sx={{
      backgroundColor: "rgba(69,144,149,0.05)",
      borderRadius: 2,
      border: "1px solid rgba(69,144,149,0.1)",
      p: 3,
    }}>
      <Stack direction="row" spacing={4} flexWrap="wrap" useFlexGap mb={ifaces.length ? 2.5 : 0}>
        <Box>
          <Typography variant="caption" color="text.secondary">Hardware</Typography>
          <Typography sx={{ fontSize: 14, color: palette.primary.main }}>
            {info?.hardware ?? "—"}
          </Typography>
        </Box>
        <Box>
          <Typography variant="caption" color="text.secondary">Firmware</Typography>
          <Typography sx={{ fontFamily: "monospace", fontSize: 14, color: palette.primary.main }}>
            {firmware ?? "—"}
          </Typography>
        </Box>
        <Box>
          <Typography variant="caption" color="text.secondary">Device Status</Typography>
          <Typography sx={{ fontSize: 14, fontWeight: 700, color: isOnline ? "#43B4B9" : "#c0392b" }}>
            {isOnline ? "Online" : "Offline"}
          </Typography>
        </Box>
        <Box>
          <Typography variant="caption" color="text.secondary">Last Update</Typography>
          <Typography sx={{ fontSize: 14, color: palette.primary.main }}>
            {info?.lastUpdate ? dayjs(info.lastUpdate).format("YYYY-MM-DD") : "—"}
          </Typography>
        </Box>
        <Box>
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.3 }}>Mirroring</Typography>
          <MirroringChip mirroring={info?.mirroring ?? null} />
        </Box>
        {info?.status?.hddSpaceFree && (
          <Box>
            <Typography variant="caption" color="text.secondary">Disk</Typography>
            <Typography sx={{ fontFamily: "monospace", fontSize: 14, color: palette.primary.main }}>
              {info.status.hddSpaceFree}
            </Typography>
          </Box>
        )}
      </Stack>

      {ifaces.length > 0 && (
        <>
          <Divider sx={{ mb: 2, borderColor: "rgba(69,144,149,0.1)" }} />
          <Typography sx={{ fontSize: "0.65rem", fontWeight: 700, color: "#555", textTransform: "uppercase", letterSpacing: "0.1em", mb: 1.5 }}>
            Network Interfaces
          </Typography>
          <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))", gap: 1.5 }}>
            {ifaces.map((iface, i) => (
              <IfaceCard key={i} iface={iface} index={i} forceOffline={!isOnline} />
            ))}
          </Box>
        </>
      )}
    </Box>
  );
};

export default DeviceInfo;
